'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard, Calendar, Car, CreditCard, Star, Users, Settings,
  ChevronLeft, ChevronRight, Bell, Search, LogOut, Menu, X,
  Briefcase, BarChart2, ClipboardList, Home
} from 'lucide-react';


interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
}

interface AppLayoutProps {
  children: React.ReactNode;
  role?: 'admin' | 'employee' | 'customer';
}

const adminNav: NavItem[] = [
  { label: 'Dashboard', href: '/admin-dashboard', icon: LayoutDashboard },
  { label: 'Appointments', href: '/admin-dashboard', icon: Calendar, badge: 5 },
  { label: 'Customers', href: '/admin-dashboard', icon: Users },
  { label: 'Vehicles', href: '/admin-dashboard', icon: Car },
  { label: 'Employees', href: '/admin-dashboard', icon: Briefcase },
  { label: 'Payments', href: '/admin-dashboard', icon: CreditCard },
  { label: 'Subscriptions', href: '/admin-dashboard', icon: Star },
  { label: 'Reports', href: '/admin-dashboard', icon: BarChart2 },
  { label: 'Settings', href: '/admin-dashboard', icon: Settings },
];

const employeeNav: NavItem[] = [
  { label: 'My Jobs', href: '/employee-portal', icon: ClipboardList, badge: 3 },
  { label: 'Schedule', href: '/employee-portal', icon: Calendar },
  { label: 'Profile', href: '/employee-portal', icon: Users },
];

const customerNav: NavItem[] = [
  { label: 'Dashboard', href: '/customer-dashboard', icon: LayoutDashboard },
  { label: 'Book Wash', href: '/book-appointment', icon: Calendar },
  { label: 'My Vehicles', href: '/customer-dashboard', icon: Car },
  { label: 'Loyalty', href: '/customer-dashboard', icon: Star },
  { label: 'Subscription', href: '/customer-dashboard', icon: CreditCard },
  { label: 'History', href: '/customer-dashboard', icon: ClipboardList },
];

export default function AppLayout({ children, role = 'customer' }: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();

  const navItems = role === 'admin' ? adminNav : role === 'employee' ? employeeNav : customerNav;

  const roleLabel = role === 'admin' ? 'Admin Panel' : role === 'employee' ? 'Employee Portal' : 'My Account';
  const roleUser = role === 'admin' ? 'Marco Martina' : role === 'employee' ? 'Jayden Rosario' : 'Keisha Martina';
  const roleEmail = role === 'admin' ? 'admin@dripndrycuracao.com' : role === 'employee' ? 'jayden@dripndrycuracao.com' : 'keisha@email.com';

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-dark text-white transition-all duration-300 ease-in-out flex-shrink-0 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-white/10">
          <AppLogo size={32} />
          {!collapsed && (
            <div className="min-w-0">
              <p className="font-bold text-sm leading-tight text-white">Drip N Dry</p>
              <p className="text-xs text-sky-400 font-medium">{roleLabel}</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const NavIcon = item.icon;
            const active = pathname === item.href;
            return (
              <Link
                key={`sidebar-${item.label}`}
                href={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 group relative ${
                  active
                    ? 'bg-primary text-white shadow-glow'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
                title={collapsed ? item.label : undefined}
              >
                <NavIcon size={18} className="flex-shrink-0" />
                {!collapsed && (
                  <span className="text-sm font-semibold flex-1">{item.label}</span>
                )}
                {!collapsed && item.badge && (
                  <span className="bg-primary text-white text-xs px-1.5 py-0.5 rounded-full font-bold">
                    {item.badge}
                  </span>
                )}
                {collapsed && item.badge && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="px-2 pb-4 border-t border-white/10 pt-3 space-y-2">
          <Link
            href="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/70 hover:bg-white/10 hover:text-white transition-all"
            title={collapsed ? 'Home' : undefined}
          >
            <Home size={18} />
            {!collapsed && <span className="text-sm font-semibold">Website</span>}
          </Link>
          <button
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/70 hover:bg-red-500/20 hover:text-red-400 transition-all w-full"
            title={collapsed ? 'Sign Out' : undefined}
          >
            <LogOut size={18} />
            {!collapsed && <span className="text-sm font-semibold">Sign Out</span>}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-center py-3 border-t border-white/10 text-white/50 hover:text-white transition-colors"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-dark text-white flex flex-col h-full">
            <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <AppLogo size={28} />
                <span className="font-bold text-sm text-white">Drip N Dry</span>
              </div>
              <button onClick={() => setMobileOpen(false)} className="text-white/70 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
              {navItems.map((item) => {
                const NavIcon = item.icon;
                return (
                  <Link
                    key={`mobile-sidebar-${item.label}`}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/70 hover:bg-white/10 hover:text-white transition-all"
                  >
                    <NavIcon size={18} />
                    <span className="text-sm font-semibold">{item.label}</span>
                    {item.badge && (
                      <span className="ml-auto bg-primary text-white text-xs px-1.5 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="bg-white border-b border-sky-100 px-4 lg:px-6 py-3 flex items-center gap-4 flex-shrink-0">
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 rounded-lg text-slate-600 hover:bg-sky-50 transition-colors"
          >
            <Menu size={20} />
          </button>

          <div className="flex-1 max-w-sm">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-9 pr-4 py-2 rounded-xl bg-sky-50 border border-sky-100 text-sm text-slate-700 placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
          </div>

          <div className="ml-auto flex items-center gap-3">
            <button className="relative p-2 rounded-xl hover:bg-sky-50 text-slate-600 transition-colors">
              <Bell size={18} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-primary flex items-center justify-center text-white text-xs font-bold">
                {roleUser.charAt(0)}
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-semibold text-slate-800 leading-tight">{roleUser}</p>
                <p className="text-xs text-muted-foreground">{roleEmail}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}